import React, { useState, useEffect } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { connect } from 'react-redux';

import { scale, env, featureEnv, updateFeatureEnv } from '@components/frame';
import { ENVIRONMENTS, ENVIRONMENTS_NAME, navigateTo, getSecureValue } from '@helpers';
import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';

import { getBoxShadowStyle } from '@styles/shadow';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { getConnectorInfo } from '@reducers/connector';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import SelectableList from './SelectableList';

const Details = (props) => {
  const {
    C,
    user: { userInfo = {} },
    getAllFeatureFlags,
    getConnectorInfo,
  } = props;
  const [loading, setLoading] = useState(false);

  const [tid, setTid] = useState('');
  const [connector, setConnector] = useState({});
  const [currentEnv, setCurrentEnv] = useState(featureEnv || env);

  useEffect(() => {
    (async () => {
      const t = getSecureValue('tid');
      setTid(t);
      const cInfo = await getConnectorInfo();
      setConnector(cInfo);
    })();
  }, []);

  const profile = [
    { name: 'Environment', value: ENVIRONMENTS_NAME[env], noArrow: true },
    { name: 'Company name', value: userInfo?.companyProfile?.companyName, noArrow: true },
    { name: 'Tenant ID', value: tid, noArrow: true },
    { name: 'User ID', value: userInfo?.userProfile?.userId, noArrow: true },
    { name: 'Connector Device Name', value: connector?.localHost, noArrow: true },
    { name: 'Connector Version', value: connector?.version, noArrow: true },
    { name: 'Connector ID', value: connector?.connectorId, noArrow: true },
    { name: 'Storage Container ID', value: userInfo?.companyProfile?.storageContainer, noArrow: true },
  ];

  const environments = [
    { name: 'Development', value: ENVIRONMENTS.DEV, key: ENVIRONMENTS.DEV },
    { name: 'QA', value: ENVIRONMENTS.QA, key: ENVIRONMENTS.QA },
    { name: 'Preview', value: ENVIRONMENTS.STG, key: ENVIRONMENTS.STG },
    { name: 'Production', value: ENVIRONMENTS.PROD, key: ENVIRONMENTS.PROD },
  ];

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View>
      <View
        style={[
          shadowStyles,
          {
            width: '100%',
            marginTop: scale(28),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
      >
        {profile.map((item, index) => (
          <Line
            {...{
              item,
              index,
              array: profile,
              C,
              roundTop: index === 0,
              roundBottom: index === profile.length - 1,
              noArrow: item.noArrow,
            }}
            key={item.name}
          />
        ))}
      </View>
      <View style={{ width: '100%', paddingLeft: scale(16), marginTop: scale(6) }}>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
          {i18n.t('The backend environment is set up for an organization upon creation and cannot be changed afterwards.')}
        </Text>
      </View>
      <View style={{ width: '100%', paddingLeft: scale(16), marginTop: scale(28), marginBottom: scale(5) }}>
        <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('FEATURE LEVEL')}</Text>
      </View>
      <View
        style={[
          shadowStyles,
          {
            width: '100%',
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
      >
        <SelectableList
          items={environments}
          selectedValue={currentEnv}
          onSelect={async (item) => {
            setLoading(true);
            await updateFeatureEnv(item.value);
            await setCurrentEnv(item.value);
            await getAllFeatureFlags();
            setLoading(false);
          }}
          loading={loading}
          C={C}
          renderTrailingContent={(item) => (
            <TouchableElement onPress={() => navigateTo('EnvironmentDetails', { env: item.key })}>
              {({}) => (
                <View style={{ justifyContent: 'center', marginRight: scale(15) }}>
                  <Svg icon="info" color="Link/Light/Primary" width={scale(19)} height={scale(19)} />
                </View>
              )}
            </TouchableElement>
          )}
        />
      </View>
      <View style={{ width: '100%', paddingLeft: scale(16), marginTop: scale(6) }}>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('WARNING:')}</Text>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
          {i18n.t(
            'The feature level is the same as the backend environment by default and it is strongly recommended not to change it. If you change the function level anyway, there is a high risk of data corruption.'
          )}
        </Text>
      </View>
    </View>
  );
};

export default connect(({ user }) => ({ user }), { getConnectorInfo })(Details);
